import { HashUtils } from "@dooboostore/core";
import { computeEmaSeries } from "./trend.js";
var TrendRange;
((TrendRange2) => {
  const buildSignalArr = (datas, sigP) => {
    let lastMacd = 0;
    const macdArr = datas.map((b) => {
      if (b.macd != null) lastMacd = b.macd;
      return lastMacd;
    });
    return computeEmaSeries(macdArr, sigP);
  };
  TrendRange2.scoreBars = (datas, config) => {
    const n = datas.length;
    const out = new Array(n).fill(null);
    if (!n) return out;
    const obvP = Math.max(2, Math.round(config?.obvPeriod ?? OBV_PERIOD));
    const sigP = Math.max(2, Math.round(config?.macdSignalMa ?? 9));
    const signalArr = buildSignalArr(datas, sigP);
    for (let i = 0; i < n; i++) {
      const b = datas[i];
      const parts = [];
      if (b.macd != null) {
        const zero = b.macd >= 0 ? 1 : 0;
        parts.push((zero + (b.macd >= signalArr[i] ? 1 : 0)) / 2);
      }
      if (b.rsi != null) parts.push(Math.max(0, Math.min(1, b.rsi / 100)));
      if (b.obv != null) {
        let sum = 0, cnt = 0;
        for (let k = Math.max(0, i - obvP + 1); k <= i; k++) {
          const v = datas[k].obv;
          if (v != null) {
            sum += v;
            cnt++;
          }
        }
        if (cnt > 0) {
          const avg = sum / cnt;
          parts.push(b.obv > avg ? 1 : b.obv < avg ? 0 : 0.5);
        }
      }
      if (parts.length) out[i] = parts.reduce((a, c) => a + c, 0) / parts.length;
    }
    return out;
  };
  TrendRange2.strengthBars = (datas, scores, config) => {
    const n = datas.length;
    const out = new Array(n).fill(null);
    if (!n) return out;
    const N = Math.max(2, Math.round(config?.strengthPeriod ?? 10));
    for (let i = 0; i < n; i++) {
      const parts = [];
      const s = scores[i];
      if (s != null) parts.push(Math.abs(s - 0.5) * 2);
      const b = datas[i];
      if (b.open != null && b.high != null && b.low != null && b.close != null && b.high > b.low) {
        parts.push(Math.max(0, Math.min(1, Math.abs(b.close - b.open) / (b.high - b.low))));
        if (i >= N) {
          let ok = true, trSum = 0;
          for (let k = i - N + 1; k <= i; k++) {
            const h = datas[k].high, l = datas[k].low;
            if (h == null || l == null || h < l) {
              ok = false;
              break;
            }
            trSum += h - l;
          }
          const c0 = datas[i - N].close, c1 = b.close;
          if (ok && trSum > 0 && c0 != null) parts.push(Math.max(0, Math.min(1, Math.abs(c1 - c0) / trSum)));
        }
      }
      if (parts.length) out[i] = parts.reduce((a, c) => a + c, 0) / parts.length;
    }
    return out;
  };
  TrendRange2.trendRanges = (datas, config) => {
    if (!datas.length) return [];
    const groupBy = config?.groupBy ?? ((rate) => String(rate));
    const q = (v) => v == null ? "_" : v.toFixed(4);
    const scores = (0, TrendRange2.scoreBars)(datas, config);
    const strengths = (0, TrendRange2.strengthBars)(datas, scores, config);
    const sigP = Math.max(2, Math.round(config?.macdSignalMa ?? 9));
    const signalArr = buildSignalArr(datas, sigP);
    const groups = scores.map((s2, i) => {
      if (s2 != null) return groupBy(s2, strengths[i] ?? 0);
      for (let k = i - 1; k >= 0; k--) if (scores[k] != null) return groupBy(scores[k], strengths[k] ?? 0);
      return groupBy(0.5, 0);
    });
    const runs = [];
    let s = 0;
    for (let i = 1; i < datas.length; i++) {
      if (groups[i] !== groups[i - 1]) {
        runs.push({ from: s, to: i - 1, group: groups[s] });
        s = i;
      }
    }
    runs.push({ from: s, to: datas.length - 1, group: groups[s] });
    const mergeCut = Math.max(1, Math.round(config?.mergeCut ?? 10));
    const materializeOne = (from, to, confirmed) => {
      let sum = 0, cnt = 0, sSum = 0, sCnt = 0;
      let raw = `${from}-${to}|`;
      for (let i = from; i <= to; i++) {
        const b = datas[i];
        if (scores[i] != null) {
          sum += scores[i];
          cnt++;
        }
        if (strengths[i] != null) {
          sSum += strengths[i];
          sCnt++;
        }
        raw += `${q(b.macd)},${q(signalArr[i])},${q(b.rsi)},${q(b.obv)},${q(b.open)},${q(b.high)},${q(b.low)},${q(b.close)},${q(scores[i])},${q(strengths[i])};`;
      }
      const rate = cnt > 0 ? sum / cnt : 0.5;
      const strength = sCnt > 0 ? sSum / sCnt : 0;
      return { startIndex: from, endIndex: to, scoreRate: rate, strengthRate: strength, group: groupBy(rate, strength), uuid: HashUtils.hash53(raw), confirmed, forecastRate: null };
    };
    const compatible = (a, b) => a.group === b.group;
    const decisiveness = (from, to) => {
      let sum = 0, cnt = 0;
      for (let i = from; i <= to; i++) {
        const s2 = scores[i];
        if (s2 == null) continue;
        sum += Math.abs(s2 - 0.5);
        cnt++;
      }
      return cnt > 0 ? sum / cnt : 0;
    };
    const decisiveCut = Math.max(0, config?.decisiveCut ?? 0.15);
    const forecastFor = (priors) => {
      if (!priors.length) return null;
      if (priors.length === 1) return Math.max(0, Math.min(1, priors[0]));
      const last3 = priors.slice(-3);
      const diffs = last3.slice(1).map((v, k) => v - last3[k]);
      const m = diffs.reduce((a, b) => a + b, 0) / diffs.length;
      return Math.max(0, Math.min(1, last3[last3.length - 1] + m));
    };
    const done = [];
    let cur = null;
    for (const r of runs) {
      if (!cur) {
        cur = { ...r };
        continue;
      }
      if (compatible(materializeOne(cur.from, cur.to, true), materializeOne(r.from, r.to, true))) {
        cur = { from: cur.from, to: r.to, group: r.group };
        continue;
      }
      if (cur.to - cur.from + 1 >= mergeCut || decisiveness(cur.from, cur.to) >= decisiveCut) {
        done.push(materializeOne(cur.from, cur.to, true));
        cur = { ...r };
      } else {
        const last = done[done.length - 1];
        if (last && materializeOne(cur.from, r.to, true).group === last.group) {
          done.push(materializeOne(cur.from, cur.to, true));
          cur = { ...r };
        } else {
          cur = { from: cur.from, to: r.to, group: r.group };
        }
      }
    }
    if (cur) done.push(materializeOne(cur.from, cur.to, false));
    for (let i = 0; i < done.length; i++) {
      done[i].forecastRate = forecastFor(done.slice(0, i).map((z) => z.scoreRate));
    }
    return done;
  };
})(TrendRange || (TrendRange = {}));
const OBV_PERIOD = 10;
export {
  TrendRange
};
//# sourceMappingURL=TrendRange.js.map
